import React, { useState } from 'react';
import { useNavigation } from '../contexts/NavigationContext';
import { useTeachingMode } from '../contexts/TeachingModeContext';
import EnhancedTeachingAids from './EnhancedTeachingAids';
import { 
  ShoppingCart, 
  ArrowLeft, 
  Activity,
  Building,
  BarChart3,
  CheckCircle,
  Package,
  Shield,
  RefreshCw,
  Receipt,
  CreditCard,
  BookOpen
} from 'lucide-react';

// Import existing components
import PurchaseOrderDashboard from './purchaseOrder/PurchaseOrderDashboard';
import PurchaseOrderList from './purchaseOrder/PurchaseOrderList';
import PurchaseOrderDetail from './purchaseOrder/PurchaseOrderDetail';
import PurchaseOrderCreate from './purchaseOrder/PurchaseOrderCreate';
import PurchaseOrderReceive from './purchaseOrder/PurchaseOrderReceive';
import SupplierRatings from './purchaseOrder/SupplierRatings';

// Import new workflow components
import InventoryManagement from './purchaseOrder/InventoryManagement';
import MRPPlanning from './purchaseOrder/MRPPlanning';
import BudgetControl from './purchaseOrder/BudgetControl';
import SupplierManagement from './purchaseOrder/SupplierManagement';
import QuotationComparison from './purchaseOrder/QuotationComparison';
import ContractManagement from './purchaseOrder/ContractManagement';
import ApprovalWorkflow from './purchaseOrder/ApprovalWorkflow';
import ReceivingManagement from './purchaseOrder/ReceivingManagement';
import QualityInspection from './purchaseOrder/QualityInspection';
import InvoiceVerification from './purchaseOrder/InvoiceVerification';
import PaymentProcessing from './purchaseOrder/PaymentProcessing';

const PurchaseOrderView = ({ navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const { goBack, cameFrom } = useNavigation();
  const { showTeachingAids } = useTeachingMode();

  // Handle saving a new purchase order
  const handleSavePO = (newPO) => {
    console.log('Saving new PO:', newPO);
    setActiveView('list');
  };

  // Handle saving a receipt
  const handleSaveReceipt = (data) => {
    console.log('Saving receipt:', data);
    setActiveView('detail');
  };

  // Handle supplier selection
  const handleSelectSupplier = (supplierId) => {
    setSelectedSupplier(supplierId);
    setActiveView('supplier_detail');
  };

  // Handle invoice selection
  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoice(invoiceId);
    setActiveView('invoice_detail');
  };

  // Navigation handlers for different workflow phases
  const handleNavigateToPhase = (phase) => {
    switch (phase) {
      // Phase A: 採購需求識別與規劃
      case 'inventory_management':
        setActiveView('inventory_management');
        break;
      case 'mrp_planning':
        setActiveView('mrp_planning');
        break;
      case 'budget_control':
        setActiveView('budget_control');
        break;
      // Phase B: 供應商評估與選擇
      case 'supplier_management':
        setActiveView('supplier_management');
        break;
      case 'quotation_comparison':
        setActiveView('quotation_comparison');
        break;
      case 'supplier_evaluation':
        setActiveView('supplier_evaluation');
        break;
      // Phase C: 採購訂單建立與發出
      case 'purchase_orders':
        setActiveView('list');
        break;
      case 'contract_management':
        setActiveView('contract_management');
        break;
      case 'approval_workflow':
        setActiveView('approval_workflow');
        break;
      // Phase D: 貨物接收與品質檢驗
      case 'receiving_management':
        setActiveView('receiving_management');
        break;
      case 'quality_inspection':
        setActiveView('quality_inspection');
        break;
      case 'inventory_update':
        setActiveView('inventory_management'); // Redirect to inventory management
        break;
      // Phase E: 發票核對與付款處理
      case 'invoice_verification':
        setActiveView('invoice_verification');
        break;
      case 'payment_processing':
        setActiveView('payment_processing');
        break;
      case 'accounting':
        setActiveView('payment_processing'); // Redirect to payment processing
        break;
      default:
        setActiveView('dashboard');
    }
  };

  const renderWorkflowBanner = () => {
    if (navigationSource === 'purchase-workflow') {
      return (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-orange-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-orange-800">來自業務流程: 採購週期管理</h3>
                <p className="text-sm text-orange-600">從需求識別到付款完成的採購管理全流程</p>
              </div>
            </div>
            <button
              onClick={() => goBack()}
              className="flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回業務流程</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <PurchaseOrderDashboard 
            onViewList={() => setActiveView('list')}
            onViewCreate={() => setActiveView('create')}
            onViewRatings={() => setActiveView('ratings')}
            onSelectPO={(po) => {
              setSelectedPO(po);
              setActiveView('detail');
            }}
            onNavigateToPhase={handleNavigateToPhase}
            navigationSource={navigationSource}
          />
        );
      case 'list':
        return (
          <PurchaseOrderList 
            onBack={() => setActiveView('dashboard')}
            onSelectPO={(po) => {
              setSelectedPO(po);
              setActiveView('detail');
            }}
            onCreatePO={() => setActiveView('create')}
          />
        );
      case 'detail':
        return (
          <PurchaseOrderDetail 
            poId={selectedPO}
            onBack={() => setActiveView('list')}
            onReceive={(poId) => {
              setSelectedPO(poId);
              setActiveView('receive');
            }}
          />
        );
      case 'create':
        return (
          <PurchaseOrderCreate 
            onBack={() => setActiveView('list')}
            onSave={handleSavePO}
          />
        );
      case 'receive':
        return (
          <PurchaseOrderReceive 
            poId={selectedPO}
            onBack={() => setActiveView('detail')}
            onSave={handleSaveReceipt}
          />
        );
      case 'ratings':
        return (
          <SupplierRatings 
            onBack={() => setActiveView('dashboard')}
          />
        );
      
      // Phase A: 採購需求識別與規劃
      case 'inventory_management':
        return (
          <InventoryManagement 
            onBack={() => setActiveView('dashboard')}
            navigationSource={navigationSource}
          />
        );
      case 'mrp_planning':
        return (
          <MRPPlanning 
            onBack={() => setActiveView('dashboard')}
            navigationSource={navigationSource}
          />
        );
      case 'budget_control':
        return (
          <BudgetControl 
            onBack={() => setActiveView('dashboard')}
            navigationSource={navigationSource}
          />
        );
      
      // Phase B: 供應商評估與選擇
      case 'supplier_management':
        return (
          <SupplierManagement 
            onBack={() => setActiveView('dashboard')}
            onSelectSupplier={handleSelectSupplier}
            navigationSource={navigationSource}
          />
        );
      case 'quotation_comparison':
        return (
          <QuotationComparison 
            onBack={() => setActiveView('dashboard')}
            navigationSource={navigationSource}
          />
        );
      case 'supplier_evaluation':
        return (
          <SupplierManagement 
            onBack={() => setActiveView('dashboard')}
            onSelectSupplier={handleSelectSupplier}
            navigationSource={navigationSource}
          />
        );
      
      // Phase C: 採購訂單建立與發出
      case 'contract_management':
        return (
          <ContractManagement 
            onBack={() => setActiveView('dashboard')}
            navigationSource={navigationSource}
          />
        );
      case 'approval_workflow':
        return (
          <ApprovalWorkflow 
            onBack={() => setActiveView('dashboard')}
            navigationSource={navigationSource}
          />
        );
      
      // Phase D: 貨物接收與品質檢驗
      case 'receiving_management':
        return (
          <ReceivingManagement 
            onBack={() => setActiveView('dashboard')}
            onSelectReceiving={(id) => {
              setSelectedPO(id);
              setActiveView('receiving_detail');
            }}
            navigationSource={navigationSource}
          />
        );
      case 'quality_inspection':
        return (
          <QualityInspection 
            onBack={() => setActiveView('dashboard')}
            navigationSource={navigationSource}
          />
        );
      
      // Phase E: 發票核對與付款處理
      case 'invoice_verification':
        return (
          <InvoiceVerification 
            onBack={() => setActiveView('dashboard')}
            onSelectInvoice={handleSelectInvoice}
            navigationSource={navigationSource}
          />
        );
      case 'payment_processing':
        return (
          <PaymentProcessing 
            onBack={() => setActiveView('dashboard')}
            navigationSource={navigationSource}
          />
        );
      default:
        return <PurchaseOrderDashboard onNavigateToPhase={handleNavigateToPhase} />;
    }
  };

  return (
    <div className="p-6">
      {renderWorkflowBanner()}
      {renderContent()}
      
      {/* Teaching Aids Panel */}
      {showTeachingAids && (
        <EnhancedTeachingAids 
          module="purchaseOrder" 
          isVisible={true}
        />
      )}
    </div>
  );
};

export default PurchaseOrderView;
